'use client'

/**
 * WatchStar — one-click add/remove a ticker to the user's watchlist, from anywhere (stock hub header,
 * screener rows, command palette). Optimistic via the shared watchlist store.
 *
 * ⭐ SIGNED-OUT VISITORS (2026-09-08): a click no longer bounces to a bare sign-in page and forgets the
 * intent. It opens SignupIntentPrompt (one-click Google) carrying a `signupIntent=watch:SYMBOL` callback;
 * when they land back here signed in, the effect below auto-adds the ticker and strips the param. This
 * closes the anon->register leak at the exact moment the reader raised their hand (see
 * findings/online-now-count-built-0908). Two looks: `icon` (bare star) and `button` (star + Watch pill).
 */

import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'
import { Star } from 'lucide-react'
import { useWatched, setWatched } from '@/lib/watchlistStore'
import SignupIntentPrompt from '@/components/SignupIntentPrompt'
import { parseIntent, urlWithoutIntent, SIGNUP_INTENT_PARAM } from '@/lib/signupIntent'

export default function WatchStar({
  symbol, variant = 'icon', className = '',
}: { symbol: string; variant?: 'icon' | 'button'; className?: string }) {
  const { status } = useSession()
  const watched = useWatched(symbol)
  const [busy, setBusy] = useState(false)
  const [prompt, setPrompt] = useState(false)

  // Complete a carried watch-intent after a one-click signup returned to this page. Reads
  // window.location directly (not useSearchParams) so WatchStar needs no Suspense boundary on the many
  // pages it renders on. Fires once status resolves to authenticated with the param present.
  useEffect(() => {
    if (status !== 'authenticated' || typeof window === 'undefined') return
    const intent = parseIntent(new URLSearchParams(window.location.search).get(SIGNUP_INTENT_PARAM))
    if (intent && intent.kind === 'watch' && intent.symbol === symbol.toUpperCase()) {
      void setWatched(symbol, true)
      window.history.replaceState(null, '', urlWithoutIntent())
    }
  }, [status, symbol])

  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (status !== 'authenticated') { setPrompt(true); return }
    if (busy) return
    setBusy(true)
    try { await setWatched(symbol, !watched) } finally { setBusy(false) }
  }

  const btn = variant === 'button' ? (
    <button type="button" onClick={onClick} disabled={busy}
      aria-pressed={watched}
      title={watched ? 'Remove from watchlist' : 'Add to watchlist'}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60 ${
        watched
          ? 'border-amber-500/50 bg-amber-500/15 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20'
          : 'border-border bg-card text-muted-foreground hover:border-amber-500/40 hover:text-amber-600 dark:hover:text-amber-300'
      } ${className}`}>
      <Star className={`h-4 w-4 ${watched ? 'fill-amber-400 text-amber-400' : ''}`} />
      {watched ? 'Watching' : 'Watch'}
    </button>
  ) : (
    <button type="button" onClick={onClick} disabled={busy}
      aria-pressed={watched}
      title={watched ? 'Remove from watchlist' : 'Add to watchlist'}
      className={`inline-flex shrink-0 items-center justify-center rounded-md p-1 transition-colors disabled:opacity-60 ${
        watched ? 'text-amber-500 dark:text-amber-400 hover:text-amber-400' : 'text-muted-foreground hover:text-amber-600 dark:hover:text-amber-300'
      } ${className}`}>
      <Star className={`h-4 w-4 ${watched ? 'fill-amber-400' : ''}`} />
    </button>
  )

  return (
    <span className="relative inline-flex">
      {btn}
      <SignupIntentPrompt open={prompt} onClose={() => setPrompt(false)} kind="watch" symbol={symbol} align={variant === 'button' ? 'left' : 'right'} />
    </span>
  )
}
